import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { Constants } from '../constants/constants';
import { User } from '../_models';
import { AESEncryptDecryptService } from '../_services/aesencrypt-decrypt.service';
import { UtilsService } from '../_services/utils.service';

/**
 * Role-based route guard for the admin/setup surface area.
 *
 * AuthGuard alone proves "someone is logged in" — that's enough for shared
 * routes but not for the admin dashboard, user management, inventory, or
 * printer/device setup. Without this guard a Server who unlocks a paired
 * register (lock-screen unlock dialog accepts Admin/Manager/Server) could
 * type `/sidebar/admin/dashboard` into the URL and land on admin pages.
 *
 * Reads `selectedRole` (the persisted active role — server-pin unlock writes
 * 'Server' here even when the underlying user has 'Admin' in role[]). The
 * route declares its allow-list via `data: { roles: [...] }`. Routes that
 * don't declare a role list are passed through (opt-in gating).
 */
@Injectable()
export class RoleGuard implements CanActivate {

    constructor(
        private router: Router,
        private _AESEncryptDecryptService: AESEncryptDecryptService,
        private utilsService: UtilsService,
    ) { }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
        const allowed: string[] | undefined = route?.data?.roles;
        if (!allowed || allowed.length === 0) {
            return true;
        }
        const user: User | undefined = this.utilsService.getCurrentUser();
        if (!user) {
            this.router.navigate(['/login'], { queryParamsHandling: 'preserve' });
            return false;
        }
        const role = user.selectedRole || (user.role && user.role[0]);
        if (role && allowed.indexOf(role) !== -1) {
            return true;
        }
        // Logged in but lacks the role for this route — bounce to the
        // register, which every staff role is allowed to use.
        this.router.navigate(['/sidebar/cashier']);
        return false;
    }
}
